// Vite adaptation of delegate-design-system/components/brand/delegate-mark.tsx.
// Only next/image is replaced with native img; asset, dimensions and styles are canonical.
import * as React from "react"
import { cn } from "cn"

/**
 * The Delegate mark: the Delegate ant (the female DelegateAnt wearing a
 * brand-teal headband, rendered front-on by delegate-avatar/delegate-ants/brand).
 * It is a full-colour, fixed-palette logo - it does not inherit `currentColor`
 * and must not be recoloured, so it is shipped as an asset (a square SVG
 * wrapping the render) rather than an inline `currentColor` SVG.
 *
 * Minimum size is 24px; below that the face collapses. Use `<BrandDots />`
 * instead when you need a mark smaller than that.
 */
function DelegateMark({
  size = 40,
  className,
  ...props
}: Omit<React.ComponentProps<"img">, "src" | "alt" | "width" | "height"> & {
  size?: number
}) {
  return (
    <img
      src="/brand/delegate-mark.svg"
      alt="Delegate Computer"
      width={size}
      height={size}
      loading="eager"
      decoding="async"
      className={cn("select-none", className)}
      {...props}
    />
  )
}

/** Mark plus wordmark, locked up at the correct optical spacing. */
function DelegateLockup({
  size = 28,
  className,
  ...props
}: React.ComponentProps<"span"> & { size?: number }) {
  return (
    <span
      data-slot="delegate-lockup"
      className={cn("inline-flex items-center gap-2.5", className)}
      {...props}
    >
      <DelegateMark size={size} />
      <span className="font-heading text-[0.95rem] font-semibold tracking-[-0.02em]">
        Delegate
      </span>
    </span>
  )
}

export { DelegateMark, DelegateLockup }
